import { createRouter, createWebHistory } from 'vue-router'

import auth from '@/middleware/auth';
import guest from '@/middleware/guest';
import adminAuth from '@/middleware/adminAuth';
import { useAuthStore } from '@/stores/auth';
import HomePage from '../views/HomePage.vue';
import RegisterPage from '../views/auth/RegisterPage.vue';
import LoginPage from '../views/auth/LoginPage.vue';
import ForgotPasswordPage from '@/views/auth/ForgotPasswordPage.vue';
import DashboardPage from '../views/user/DashboardPage.vue';
import OrganizationsList from '../views/user/organizations/ListPage.vue';
import ProfilePage from '../views/user/ProfilePage.vue';
import NotFoundPage from '../views/NotFoundPage.vue';
import OrgOverviewPage from '@/views/user/organizations/OverviewPage.vue';
import OrgLearnersPage from '@/views/user/organizations/LearnersPage.vue';
import OrgViewPage from '@/views/user/organizations/ViewPage.vue';
import LearnerDetailPage from '@/views/user/organizations/LearnerDetailPage.vue';
import AdministratorsPage from '@/views/user/organizations/AdministratorsPage.vue';
import GroupsPage from '@/views/user/organizations/GroupsPage.vue';
import GroupDetailPage from '@/views/user/organizations/GroupDetailPage.vue';
import ProductsPage from '@/views/user/organizations/ProductsPage.vue';
import CartPage from '@/views/user/organizations/CartPage.vue';
import PurchaseHistoryPage from '@/views/user/organizations/PurchaseHistoryPage.vue';
import LearnerAssessmentPage from '@/views/user/organizations/LearnerAssessmentPage.vue';
import LearnerDashboard from '@/views/user/organizations/LearnerDashboard.vue';
import LearnerAssessmentsPage from '@/views/user/organizations/LearnerAssessmentsPage.vue';
import { useErrorStore } from '@/stores/errorStore';
import LearnerVoiceBankPage from '@/views/user/organizations/LearnerVoiceBankPage.vue';
import LearnerVoiceBankTestItem from '@/views/user/organizations/LearnerVoiceBankTestItem.vue';
import LearnersPage from '@/views/user/LearnersPage.vue';
import LearnerWorkpacketPage from '@/views/user/organizations/LearnerWorkpacketPage.vue';
import LearnerWorkpacketDetailPage from '@/views/user/organizations/LearnerWorkpacketDetailPage.vue';
import LearnerWorkpacketGuidedPage from '@/views/user/organizations/LearnerWorkpacketGuidedPage.vue';
import ProfileSettingPage from '@/views/user/ProfileSettingPage.vue';
import RestoreAccountPage from '@/views/RestoreAccountPage.vue';
import PrivacyPage from '@/views/PrivacyPage.vue';
import TermsAndConditions from '@/views/TermsAndConditions.vue';
import ChangePasswordPage from '@/views/user/ChangePasswordPage.vue';
import UnauthorizedPage from '@/views/UnauthorizedPage.vue';
// Admin
import AdminDashboardPage from '@/views/admin/DashboardPage.vue';
import AdminOrdersPage from '@/views/admin/OrdersPage.vue';
import AdminOrderDetailPage from '@/views/admin/OrderDetailPage.vue';
import AdminUsersPage from '@/views/admin/UsersPage.vue';
import AdminUserDetailPage from '@/views/admin/UserDetailPage.vue';
import AdminOrganizationsPage from '@/views/admin/OrganizationsPage.vue';
import AdminOrganizationDetailPage from '@/views/admin/OrganizationDetailPage.vue';
import AdminOverviewPage from '@/views/admin/organization/OverviewPage.vue';
import AdminLearnersPage from '@/views/admin/organization/LearnersPage.vue';
import AdminLearnerDetailPage from '@/views/admin/organization/LearnerDetailPage.vue';
import AdminAdministratorsPage from '@/views/admin/organization/AdministratorsPage.vue';
import AdminGroupsPage from '@/views/admin/organization/GroupsPage.vue';
import AdminGroupDetailPage from '@/views/admin/organization/GroupDetailPage.vue';
import AdminPurchaseHistoryPage from '@/views/admin/organization/PurchaseHistoryPage.vue';
import AdminTranscriptionFeedback from '@/views/admin/AdminTranscriptionFeedback.vue';
import AgreementsPage from '@/views/user/organizations/AgreementsPage.vue';

const routes = [
    {
        path: '/',
        name: 'Home',
        component: HomePage,
        meta: {
            title: 'Home | EESAPP APP',
        }
    },
    {
        path: '/register',
        name: 'Register',
        component: RegisterPage,
        meta: {
            title: 'Register | EESAPP APP',
            layout: 'secondary',
            middleware: ['guest']
        }
    },
    {
        path: '/login',
        name: 'Login',
        component: LoginPage,
        meta: {
            title: 'Login | EESAPP APP',
            layout: 'secondary',
            middleware: ['guest']
        }
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: ForgotPasswordPage,
        meta: {
            title: 'Forgot Password | EESAPP APP',
            layout: 'secondary',
            middleware: ['guest']
        }
    },
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: DashboardPage,
        meta: {
            title: 'Dashboard | EESAPP APP',
            middleware: ['auth']
        }
    },
    {
        path: '/organizations',
        component: () => import('@/layouts/RouteView.vue'),
        meta: {
            title: 'Organizations | EESAPP APP',
            middleware: ['auth']
        },
        children: [
            {
                path: '',
                name: 'Organizations',
                component: OrganizationsList,
                meta: {
                    title: 'Organizations | EESAPP APP',
                    middleware: ['auth']
                },
            },
            {
                path: ':organization',
                component: () => import('@/layouts/RouteView.vue'),
                children: [
                    {
                        path: '',
                        component: OrgViewPage,
                        meta: {
                            title: 'Organization Overview | EESAPP APP',
                            middleware: ['auth']
                        },
                        redirect: to => {
                            return { name: 'orgOverview', params: { organization: to.params.organization } };
                        },
                        children: [
                            {
                                path: 'overview',
                                name: 'OrgOverview',
                                component: OrgOverviewPage,
                                meta: {
                                    title: 'Organization Overview | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            // Learner routes
                            {
                                path: 'learners',
                                component: () => import('@/layouts/RouteView.vue'),
                                meta: {
                                    title: 'Learners | EESAPP APP',
                                    middleware: ['auth']
                                },
                                children: [
                                    {
                                        path: '',
                                        name: 'OrgLearners',
                                        component: OrgLearnersPage,
                                        meta: {
                                            title: 'Learners | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    },
                                    {
                                        path: ':learner',
                                        component: () => import('@/layouts/RouteView.vue'),
                                        meta: {
                                            title: 'Learner Details | EESAPP APP',
                                            middleware: ['auth']
                                        },
                                        children: [
                                            {
                                                path: '',
                                                name: 'LearnerDetail',
                                                component: LearnerDetailPage,
                                                meta: {
                                                    title: 'Learner Details | EESAPP APP',
                                                    middleware: ['auth']
                                                }
                                            },
                                        ]
                                    }
                                ]
                            },
                            {
                                path: 'administrator',
                                name: 'OrgAdministrators',
                                component: AdministratorsPage,
                                meta: {
                                    title: 'Administrators | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },

                            // Group routes
                            {
                                path: 'group',
                                component: () => import('@/layouts/RouteView.vue'),
                                meta: {
                                    title: 'Groups | EESAPP APP',
                                    middleware: ['auth']
                                },
                                children: [
                                    {
                                        path: '',
                                        name: 'OrgGroups',
                                        component: GroupsPage,
                                        meta: {
                                            title: 'Groups | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    },
                                    {
                                        path: ':groupId',
                                        name: 'OrgGroupDetail',
                                        component: GroupDetailPage,
                                        meta: {
                                            title: 'Group Detail | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    }
                                ]
                            },

                            // Subscription routes
                            {
                                path: 'subscription',
                                component: () => import('@/layouts/RouteView.vue'),
                                meta: {
                                    title: 'Subscriptions | EESAPP APP',
                                    middleware: ['auth']
                                },
                                children: [
                                    {
                                        path: '',
                                        name: 'OrgProducts',
                                        component: ProductsPage,
                                        meta: {
                                            title: 'Subscriptions | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    },
                                    {
                                        path: 'cart',
                                        name: 'OrgCart',
                                        component: CartPage,
                                        meta: {
                                            title: 'Cart | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    },
                                    {
                                        path: 'purchase-history',
                                        name: 'OrgPurchaseHistory',
                                        component: PurchaseHistoryPage,
                                        meta: {
                                            title: 'Purchase History | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    }
                                ]
                            },
                            
                            // Agreements routes
                            {
                                path: 'agreements',
                                component: () => import('@/layouts/RouteView.vue'),
                                meta: {
                                    title: 'Agreements | EESAPP APP',
                                    middleware: ['auth']
                                },
                                children: [
                                    {
                                        path: '',
                                        name: 'Agreements',
                                        component: AgreementsPage,
                                        meta: {
                                            title: 'Agreements | EESAPP APP',
                                            middleware: ['auth']
                                        }
                                    },
                                ]
                            },
                        ]
                    },
                    {
                        path: 'learners/:learner',
                        component: () => import('@/layouts/RouteView.vue'),
                        meta: {
                            middleware: ['auth']
                        },
                        children: [
                            {
                                path: 'dashboard',
                                name: 'LearnerDashboard',
                                component: LearnerDashboard,
                                meta: {
                                    title: 'Learner Dashboard | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'assessments',
                                name: 'LearnerAssessments',
                                component: LearnerAssessmentsPage,
                                meta: {
                                    title: 'Learner Assessments | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'assessments/:assessment',
                                name: 'LearnerAssessment',
                                component: LearnerAssessmentPage,
                                meta: {
                                    title: 'Assessment Details | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'voice-bank',
                                name: 'LearnerVoiceBank',
                                component: LearnerVoiceBankPage,
                                meta: {
                                    title: 'Voice Bank | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'voice-bank/:assessment',
                                name: 'LearnerAssessmentVoiceBank',
                                component: LearnerVoiceBankPage,
                                meta: {
                                    title: 'Voice Bank | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'voice-bank/testitem',
                                name: 'LearnerVoiceBankTestItem',
                                component: LearnerVoiceBankTestItem,
                                meta: {
                                    title: 'Voice Bank | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'workpacket',
                                name: 'LearnerWorkpacket',
                                component: LearnerWorkpacketPage,
                                meta: {
                                    title: 'Work Packet | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'workpacket/:assessment',
                                name: 'LearnerWorkpacketDetail',
                                component: LearnerWorkpacketDetailPage,
                                meta: {
                                    title: 'Work Packet | EESAPP APP',
                                    middleware: ['auth']
                                }
                            },
                            {
                                path: 'workpacket/:assessment/guided',
                                name: 'LearnerWorkpacketGuided',
                                component: LearnerWorkpacketGuidedPage,
                                meta: {
                                    title: 'Guided Plan | EESAPP APP',
                                    middleware: ['auth']
                                }
                            }
                        ]
                    }

                ],
            },

        ],
    },
    {
        path: '/learners',
        name: 'Learners',
        component: LearnersPage,
        meta: {
            title: 'Learners | EESAPP APP',
            middleware: ['auth']
        }
    },
    {
        path: '/profile',
        name: 'Profile',
        component: ProfilePage,
        meta: {
            title: 'Profile | EESAPP APP',
            middleware: ['auth']
        }
    },
    {
        path: '/change-password',
        name: 'ProfiChangePassword',
        component: ChangePasswordPage,
        meta: {
            title: 'Change Password | EESAPP APP',
            middleware: ['auth']
        }
    },
    {
        path: '/account-setting',
        name: 'ProfileSetting',
        component: ProfileSettingPage,
        meta: {
            title: 'Account Settings | EESAPP APP',
            middleware: ['auth']
        }
    },
    {
        path: '/restore-account/:token',
        name: 'RestoreAccount',
        component: RestoreAccountPage,
        meta: {
            title: 'Restore Account | EESAPP APP',
            layout: 'secondary',
            middleware: ['guest']
        }
    },
    {
        path: '/privacy-policy',
        name: 'PrivacyPolicy',
        component: PrivacyPage,
        meta: {
            title: 'Privacy Policy | EESAPP APP',
        }
    },
    {
        path: '/terms-conditions',
        name: 'TermsConditions',
        component: TermsAndConditions,
        meta: {
            title: 'Terms & Conditions | EESAPP APP',
        }
    },
    {
        path: '/pricing',
        name: 'Pricing',
        component: () => import('@/views/PricingPage.vue'),
        meta: {
            title: 'Pricing | EESAPP APP',
        }
    },
    {
        path: '/unauthorized',
        name: 'Unauthorized',
        component: UnauthorizedPage,
        meta: {
            title: 'Unauthorized | EESAPP APP',
        }
    },
    {
        path: '/:pathMatch(.*)*',
        name: '404',
        component: NotFoundPage,
        meta: { title: '404 | Page Not Found' }
    },
    // admin
    {
        path: '/admin',
        component: () => import('@/layouts/AdminDashboardLayout.vue'),
        meta: {
            title: 'Admin | EESAPP APP',
            middleware: ['adminAuth']
        },
        children: [
            {
                path: 'dashboard',
                name: 'AdminDashboard',
                component: AdminDashboardPage,
                meta: {
                    title: 'Admin Dashboard | EESAPP APP',
                    middleware: ['adminAuth']
                },
            },
            {
                path: 'users',
                name: 'AdminUsers',
                component: AdminUsersPage,
                meta: {
                    title: 'Admin Users Management | EESAPP APP',
                    middleware: ['adminAuth']
                },
            },
            {
                path: 'users/:userid',
                name: 'AdminUserDetail',
                component: AdminUserDetailPage,
                meta: {
                    title: 'Admin User Detail | EESAPP APP',
                    middleware: ['adminAuth']
                },
                children: [
                    {
                        path: '',
                        name: "AdminPurchaseHistory",
                        component: AdminPurchaseHistoryPage,
                        meta: {
                            title: 'Admin User Purchase History | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'groups',
                        name: "AdminGroups",
                        component: AdminGroupsPage,
                        meta: {
                            title: 'Admin User Groups | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                ]
            },
            {
                path: 'organizations',
                name: 'AdminOrganizations',
                component: AdminOrganizationsPage,
                meta: {
                    title: 'Admin Organizations Management | EESAPP APP',
                    middleware: ['adminAuth']
                },
            },
            {
                path: 'organizations/:organization',
                name: 'AdminOrgDetail',
                component: AdminOrganizationDetailPage,
                meta: {
                    title: 'Admin Organization Detail | EESAPP APP',
                    middleware: ['adminAuth']
                },
                children: [
                    {
                        path: '',
                        component: AdminOverviewPage,
                        name: "AdminOverview",
                        meta: {
                            title: 'Admin Organization Detail | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'learners',
                        name: 'AdminOrgLearners',
                        component: AdminLearnersPage,
                        meta: {
                            title: 'Admin Organization Learners | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'learners/:learner',
                        name: 'AdminOrgLearnerDetail',
                        component: AdminLearnerDetailPage,
                        meta: {
                            title: 'Admin Organization Learners | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'administrator',
                        name: 'AdminOrgAdministrator',
                        component: AdminAdministratorsPage,
                        meta: {
                            title: 'Admin Organization Administrators | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'group',
                        name: 'AdminOrgGroups',
                        component: AdminGroupsPage,
                        meta: {
                            title: 'Admin Organization Groups | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'group/:groupId',
                        name: 'AdminOrgGroupDetail',
                        component: AdminGroupDetailPage,
                        meta: {
                            title: 'Admin Organization Group Detail | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                    {
                        path: 'purchase-history',
                        name: 'AdminOrgPurchaseHistory',
                        component: AdminPurchaseHistoryPage,
                        meta: {
                            title: 'Admin Organization Group Detail | EESAPP APP',
                            middleware: ['adminAuth']
                        },
                    },
                ]
            },
            {
                path: 'orders',
                name: 'AdminOrders',
                component: AdminOrdersPage,
                meta: {
                    title: 'Admin Orders Management | EESAPP APP',
                    middleware: ['adminAuth']
                },
            },
            {
                path: 'orders/:orderid',
                name: 'AdminOrderDetail',
                component: AdminOrderDetailPage,
                meta: {
                    title: 'Admin Order Detail | EESAPP APP',
                    middleware: ['adminAuth']
                },
            },
            {
                path: 'feedback',
                name: 'AdminTranscriptionFeedback',
                component: AdminTranscriptionFeedback,
                meta: {
                    title: 'Admin Transcription Feedback | EESAPP APP',
                    middleware: ['adminAuth']
                },
            },
        ]
    }
]

const router = createRouter({
    history: createWebHistory(), // uses HTML5 history mode
    routes,
    scrollBehavior(to) {
        if (to?.hash) {
            const el = document.querySelector(to.hash);
            if (el) {
                return { el, behavior: 'smooth' };
            }
        }
        return { top: 0 };
    }
})

const middlewareMap = {
    auth,
    adminAuth,
    guest
};

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();

    if(!authStore.user) {
        await authStore.fetchUser();
    }

    // If no middleware defined → proceed
    if (!to.meta.middleware) {
        return next();
    }

    // Convert to array if needed
    const middlewareArray = Array.isArray(to.meta.middleware)
        ? to.meta.middleware
        : [to.meta.middleware];

    // Prepare context for middleware
    const context = {
        to,
        from,
        next,
        store: authStore
    };

    // Map middleware names to actual middleware functions
    const stack = middlewareArray.map(name => {
        const middlewareFn = middlewareMap[name];
        if (!middlewareFn) {
            console.warn(`Middleware "${name}" is not defined in middlewareMap.`);
        }
        return middlewareFn;
    }).filter(Boolean); // Filter out undefined middleware

    // Recursive _next
    const _next = (params) => {
        // If middleware wants to redirect → do it now
        if (params) {
            return next(params);
        }

        // Get next middleware in stack
        const middleware = stack.shift();

        if (middleware) {
            // Run middleware and pass _next
            middleware({ ...context, next: _next });
        } else {
            // All middleware passed → proceed
            next();
        }
    };

    // Start the middleware chain
    _next();
});

router.afterEach((to) => {
    useErrorStore().reset();

    // First, remove any previously added route-* class
    document.body.classList.forEach((cls) => {
        if (cls.startsWith('route-')) {
            document.body.classList.remove(cls);
        }
    });

    // Add new route name as class (if exists)
    if (to.name) {
        document.body.classList.add(`route-${to.name}`);
    }
});

export default router